import api from './api';

/**
 * Display rounding for day balances (HW-686): whole number when round,
 * one decimal otherwise — e.g. 12 → "12", -4.6999… → "-4.7".
 */
export const formatDays = (n: number): string => n.toFixed(n % 1 === 0 ? 0 : 1);

// ============================================
// ABSENCE TYPES
// ============================================

export interface AbsenceTypeTenureLevel {
  id: string;
  absenceTypeId: string;
  tenureYears: number;
  additionalDays: number;
  createdAt: string;
}

export interface AbsenceTypeApprover {
  id: string;
  absenceTypeId: string;
  employeeId: string;
  employee?: AbsenceTypeEmployee;
  createdAt: string;
}

export interface AbsenceTypeAssignment {
  id: string;
  employeeId?: string;
  departmentId?: string;
}

export interface AbsenceTypeEmployee {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  workEmail: string;
  avatarUrl?: string | null;
  department?: {
    id: string;
    name: string;
  } | null;
  position?: {
    id: string;
    title: string;
  } | null;
}

export interface AbsenceType {
  id: string;
  name: string;
  isPaid: boolean;
  defaultDays: number;
  color?: string;

  // Basics
  countingMethod: 'working_days' | 'calendar_days';
  isActive: boolean;
  instructions?: string;

  // Accrual
  accrualEnabled: boolean;
  accrualAmount: number;
  accrualIntervalCount: number;
  accrualInterval: 'day' | 'week' | 'month' | 'quarter' | 'half_year' | 'year';
  accrualTiming: 'start' | 'end';
  accrualYearType: 'individual' | 'calendar';
  firstAccrualCalculation: 'full' | 'proportional';
  startAccrual: 'immediately' | 'after_probation' | 'after_delay';
  startDelayDays: number;
  countProbationInAccrual: boolean;
  initialBalance: number;

  // Carry-over & Limits
  unlimitedBalance: boolean;
  maxBalance: number;
  allowNegativeBalance: boolean;
  // [HW-689]
  allowAdvanceAgainstAccrual: boolean;
  carryOverModel: 'ttl' | 'year_based';
  // `null` is the intentional "infinite availability" sentinel (Accrued
  // days never expire). The shared `@hygge/shared` AbsenceType already
  // models this as `number | null` — keeping the local copy in sync so
  // `Partial<AbsenceTypeWritable>` accepts `null` from the UI toggle.
  availabilityPeriodDays?: number | null;
  yearBasedAvailability: 'end_of_next_accrual' | 'end_of_next_calendar';
  afterAvailabilityWindow: 'expire' | 'carry_over_with_limit';
  carryOverLimit?: number;
  carriedDaysExpireAfterDays?: number;

  // Request Rules
  enableOnDemand: boolean;
  onDemandAmount: number;
  preventOverlaps: boolean;
  allowHalfDays: boolean;
  minRequestAmount: number;
  // These four are stored as nullable `Int?` / `Float?` in Postgres and the
  // shared `@hygge/shared` AbsenceType already models them as `number | null`.
  // The local copy used to type them as `number | undefined` only — which
  // meant the configurator form sent `undefined` to clear them, and
  // `JSON.stringify` then DROPPED the field from the PATCH body entirely,
  // so the backend never saw the clear intent and the column stayed at its
  // old value (the "Auto-approve after time toggle won't turn off" bug).
  // Including `null` here lets the form explicitly NULL the columns.
  maxRequestAmount?: number | null;
  minNoticeDays: number;
  maxAdvanceDays?: number | null;
  allowedDuringProbation: 'not_allowed' | 'allowed' | 'allowed_with_limit';
  probationLimit?: number;
  employeeCanSubmit: boolean;

  // Approvals
  approvalRequired: boolean;
  managerCanApprove: boolean;
  hrCanApprove: boolean;
  autoApproveIfNoApprover: boolean;
  autoApproveAfterDays?: number | null;

  // Visibility & Notifications
  showInCalendar: boolean;
  emailNotifications: boolean;
  notifyApprovers: boolean;
  notifyRequester: boolean;
  attachmentMode: 'disabled' | 'optional' | 'required';
  allowRecall: boolean;
  recallDeadlineDays?: number | null;
  requesterCommentRequired: boolean;
  approverCommentRequired: boolean;
  approverNotificationScope: 'only_assigned' | 'all_assigned';
  specificApproversEnabled: boolean;
  substituteApproversEnabled: boolean;

  // Relations
  tenureLevels?: AbsenceTypeTenureLevel[];
  assignments?: AbsenceTypeAssignment[];
  specificApprovers?: AbsenceTypeApprover[];
  substituteApprovers?: AbsenceTypeApprover[];
  _count?: { assignments: number };

  createdAt: string;
  updatedAt: string;
}

// Fields the configurator/create form can submit. Derived from AbsenceType so
// every typed field stays in sync; we just drop server-managed/relations fields.
type AbsenceTypeWritable = Omit<
  AbsenceType,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'tenureLevels'
  | 'assignments'
  | 'specificApprovers'
  | 'substituteApprovers'
  | '_count'
>;

export type CreateAbsenceTypeInput = { name: string } & Partial<Omit<AbsenceTypeWritable, 'name'>>;

export type UpdateAbsenceTypeInput = Partial<AbsenceTypeWritable>;

// API Functions

export async function getAbsenceTypes(): Promise<AbsenceType[]> {
  const { data } = await api.get<AbsenceType[]>('/absence-types');
  return data;
}

export async function getAbsenceType(id: string): Promise<AbsenceType> {
  const { data } = await api.get<AbsenceType>(`/absence-types/${id}`);
  return data;
}

export async function createAbsenceType(input: CreateAbsenceTypeInput): Promise<AbsenceType> {
  const { data } = await api.post<AbsenceType>('/absence-types', input);
  return data;
}

export async function updateAbsenceType(
  id: string,
  input: UpdateAbsenceTypeInput
): Promise<AbsenceType> {
  const { data } = await api.put<AbsenceType>(`/absence-types/${id}`, input);
  return data;
}

export async function deleteAbsenceType(id: string): Promise<void> {
  await api.delete(`/absence-types/${id}`);
}

export async function toggleAbsenceTypeActive(id: string): Promise<{ isActive: boolean }> {
  const { data } = await api.patch<{ isActive: boolean }>(`/absence-types/${id}/toggle-active`);
  return data;
}

export async function duplicateAbsenceType(id: string, name: string): Promise<AbsenceType> {
  const { data } = await api.post<AbsenceType>(`/absence-types/${id}/duplicate`, { name });
  return data;
}

export async function getAbsenceTypeEmployees(id: string): Promise<AbsenceTypeEmployee[]> {
  const { data } = await api.get<AbsenceTypeEmployee[]>(`/absence-types/${id}/employees`);
  return data;
}

export async function unassignEmployeeFromAbsenceType(
  absenceTypeId: string,
  employeeId: string
): Promise<void> {
  await api.delete(`/absence-types/${absenceTypeId}/employees/${employeeId}`);
}

// ============================================
// TENURE LEVELS
// ============================================

export async function addAbsenceTypeTenureLevel(
  absenceTypeId: string,
  tenureYears: number,
  additionalDays: number
): Promise<AbsenceTypeTenureLevel> {
  const { data } = await api.post<AbsenceTypeTenureLevel>(
    `/absence-types/${absenceTypeId}/tenure-levels`,
    { tenureYears, additionalDays }
  );
  return data;
}

export async function updateAbsenceTypeTenureLevel(
  levelId: string,
  tenureYears: number,
  additionalDays: number
): Promise<AbsenceTypeTenureLevel> {
  const { data } = await api.put<AbsenceTypeTenureLevel>(
    `/absence-types/tenure-levels/${levelId}`,
    { tenureYears, additionalDays }
  );
  return data;
}

export async function deleteAbsenceTypeTenureLevel(levelId: string): Promise<void> {
  await api.delete(`/absence-types/tenure-levels/${levelId}`);
}

// ============================================
// ASSIGNMENTS
// ============================================

export interface AbsenceTypeEmploymentTypeEmployee extends AbsenceTypeEmployee {
  viaEmploymentTypeId: string | null;
  viaEmploymentTypeName: string | null;
}

export interface AbsenceTypeAssignmentsResponse {
  employees: AbsenceTypeEmployee[];
  departments: { id: string; name: string; _count: { employees: number } }[];
  employmentTypes: { id: string; name: string; _count: { employees: number } }[];
  employmentTypeEmployees: AbsenceTypeEmploymentTypeEmployee[];
}

export async function getAbsenceTypeAssignments(
  absenceTypeId: string
): Promise<AbsenceTypeAssignmentsResponse> {
  const { data } = await api.get<AbsenceTypeAssignmentsResponse>(
    `/absence-types/${absenceTypeId}/assignments`
  );
  return data;
}

export async function assignAbsenceTypeToEmployee(
  absenceTypeId: string,
  employeeId: string,
  effectiveFrom?: string
): Promise<void> {
  await api.post(`/absence-types/${absenceTypeId}/assignments/employee/${employeeId}`, {
    effectiveFrom,
  });
}

export async function assignAbsenceTypeToDepartment(
  absenceTypeId: string,
  departmentId: string
): Promise<AbsenceTypeAssignment> {
  const { data } = await api.post<AbsenceTypeAssignment>(
    `/absence-types/${absenceTypeId}/assignments/department/${departmentId}`
  );
  return data;
}

export async function assignAbsenceTypeToEmploymentType(
  absenceTypeId: string,
  employmentTypeId: string
): Promise<AbsenceTypeAssignment> {
  const { data } = await api.post<AbsenceTypeAssignment>(
    `/absence-types/${absenceTypeId}/assignments/employment-type/${employmentTypeId}`
  );
  return data;
}

export async function unassignAbsenceTypeFromEmploymentType(
  absenceTypeId: string,
  employmentTypeId: string
): Promise<void> {
  await api.delete(
    `/absence-types/${absenceTypeId}/assignments/employment-type/${employmentTypeId}`
  );
}

export async function removeAbsenceTypeAssignment(assignmentId: string): Promise<void> {
  await api.delete(`/absence-types/assignments/${assignmentId}`);
}

export interface BatchAssignmentResult {
  added: number;
  removed: number;
  total: number;
}

export async function batchAssignAbsenceType(
  absenceTypeId: string,
  employeeIds: string[]
): Promise<BatchAssignmentResult> {
  const { data } = await api.put<BatchAssignmentResult>(
    `/absence-types/${absenceTypeId}/assignments/batch`,
    { employeeIds }
  );
  return data;
}

export async function assignAbsenceTypeToAll(
  absenceTypeId: string
): Promise<{ assigned: number; total: number }> {
  const { data } = await api.post<{ assigned: number; total: number }>(
    `/absence-types/${absenceTypeId}/assignments/all`
  );
  return data;
}

export async function unassignAbsenceTypeFromAll(
  absenceTypeId: string
): Promise<{ removed: number }> {
  const { data } = await api.delete<{ removed: number }>(
    `/absence-types/${absenceTypeId}/assignments/all`
  );
  return data;
}

// ============================================
// SPECIFIC APPROVERS
// ============================================

export async function getSpecificApprovers(absenceTypeId: string): Promise<AbsenceTypeApprover[]> {
  const { data } = await api.get<AbsenceTypeApprover[]>(
    `/absence-types/${absenceTypeId}/specific-approvers`
  );
  return data;
}

export async function addSpecificApprover(
  absenceTypeId: string,
  employeeId: string
): Promise<AbsenceTypeApprover> {
  const { data } = await api.post<AbsenceTypeApprover>(
    `/absence-types/${absenceTypeId}/specific-approvers/${employeeId}`
  );
  return data;
}

export async function removeSpecificApprover(
  absenceTypeId: string,
  employeeId: string
): Promise<void> {
  await api.delete(`/absence-types/${absenceTypeId}/specific-approvers/${employeeId}`);
}

export async function batchUpdateSpecificApprovers(
  absenceTypeId: string,
  employeeIds: string[]
): Promise<BatchAssignmentResult> {
  const { data } = await api.put<BatchAssignmentResult>(
    `/absence-types/${absenceTypeId}/specific-approvers/batch`,
    { employeeIds }
  );
  return data;
}

// ============================================
// SUBSTITUTE APPROVERS
// ============================================

export async function getSubstituteApprovers(
  absenceTypeId: string
): Promise<AbsenceTypeApprover[]> {
  const { data } = await api.get<AbsenceTypeApprover[]>(
    `/absence-types/${absenceTypeId}/substitute-approvers`
  );
  return data;
}

export async function addSubstituteApprover(
  absenceTypeId: string,
  employeeId: string
): Promise<AbsenceTypeApprover> {
  const { data } = await api.post<AbsenceTypeApprover>(
    `/absence-types/${absenceTypeId}/substitute-approvers/${employeeId}`
  );
  return data;
}

export async function removeSubstituteApprover(
  absenceTypeId: string,
  employeeId: string
): Promise<void> {
  await api.delete(`/absence-types/${absenceTypeId}/substitute-approvers/${employeeId}`);
}

export async function batchUpdateSubstituteApprovers(
  absenceTypeId: string,
  employeeIds: string[]
): Promise<BatchAssignmentResult> {
  const { data } = await api.put<BatchAssignmentResult>(
    `/absence-types/${absenceTypeId}/substitute-approvers/batch`,
    { employeeIds }
  );
  return data;
}

// ============================================
// EMPLOYEE ASSIGNED ABSENCE TYPES
// ============================================

export interface AssignedAbsenceTypeBalance {
  available: number | null;
  used: number;
  pending: number;
  accrued: number | null;
  carriedOver: number;
  unit: 'days' | 'hours';
}

export interface AssignedAbsenceTypeAccrual {
  nextDate: string | null;
  nextAmount: number;
  pastAccruals?: { date: string; amount: number }[];
}

export interface AssignedAbsenceTypeRequestRules {
  minNoticeDays: number;
  maxAdvanceDays: number | null;
  minRequestAmount: number;
  maxRequestAmount: number | null;
  allowNegativeBalance: boolean;
  // [HW-689] When on, an over-balance request is accepted for HR review (not
  // rejected) if an imminent accrual covers it before the start date.
  allowAdvanceAgainstAccrual: boolean;
  preventOverlaps: boolean;
  allowedDuringProbation: 'not_allowed' | 'allowed' | 'allowed_with_limit';
  probationLimit: number | null;
  requesterCommentRequired: boolean;
  instructions: string | null;
  enableOnDemand: boolean;
  onDemandAmount: number;
}

export interface EmployeeAssignedAbsenceType {
  assignmentId: string | null;
  assignmentType: 'individual' | 'department' | 'employment_type' | 'historical';
  isInactive?: boolean;
  effectiveFrom: string | null;
  assignedAt: string;
  absenceType: {
    id: string;
    name: string;
    color: string;
    isPaid: boolean;
    isActive?: boolean;
    countingMethod: 'working_days' | 'calendar_days';
    allowHalfDays: boolean;
    employeeCanSubmit: boolean;
    approvalRequired: boolean;
    attachmentMode: 'disabled' | 'optional' | 'required';
    unlimitedBalance: boolean;
    requestRules?: AssignedAbsenceTypeRequestRules;
  };
  balance: AssignedAbsenceTypeBalance;
  accrual: AssignedAbsenceTypeAccrual | null;
}

export interface EmployeeAssignedAbsenceTypesResponse {
  data: EmployeeAssignedAbsenceType[];
  meta: {
    totalAbsenceTypes: number;
    employeeTenureYears?: number;
  };
}

export async function getEmployeeAssignedAbsenceTypes(
  employeeId: string,
  year?: number
): Promise<EmployeeAssignedAbsenceTypesResponse> {
  const { data } = await api.get<EmployeeAssignedAbsenceTypesResponse>(
    `/absences/employee/${employeeId}/assigned-policies`,
    year != null ? { params: { year } } : undefined
  );
  return data;
}

// ============================================
// ACCRUAL EXPLANATION
// ============================================

export interface AccrualExplanation {
  config: {
    accrualEnabled: boolean;
    accrualAmount: number;
    accrualInterval: string;
    accrualIntervalCount: number;
    accrualTiming: string;
    accrualYearType: string;
    firstAccrualCalculation: string;
    startAccrual: string;
    startDelayDays: number;
    initialBalance: number;
  };
  employee: {
    startDate: string;
    probationEndDate: string | null;
    tenureYears: number;
  };
  calculation: {
    accrualStartDate: string;
    accrualStartReason: string;
    periodsElapsed: number;
    periodLengthDays: number;
    baseAmount: number;
    isProrated: boolean;
    proratedAmount: number;
    prorationFactor: number | null;
    tenureBonus: number;
    tenureBonusReason: string | null;
    initialBalance: number;
    totalAccrued: number;
  };
  currentBalance: {
    year: number;
    accruedDays: number;
    usedDays: number;
    pendingDays: number;
    carriedOverDays: number;
    available: number;
    importedFromPfDays: number | null;
    importedAt: string | null;
  } | null;
  schedule: Array<{
    date: string;
    amount: number;
    cumulativeTotal: number;
  }>;
  nextAccrual: {
    date: string;
    amount: number;
  } | null;
}

export async function getAccrualExplanation(
  employeeId: string,
  absenceTypeId: string
): Promise<AccrualExplanation> {
  const { data } = await api.get<AccrualExplanation>(
    `/absence-balances/employee/${employeeId}/policy/${absenceTypeId}/accrual-explanation`
  );
  return data;
}

export async function recalculateBalance(
  employeeId: string,
  absenceTypeId: string,
  year?: number
): Promise<unknown> {
  const params = year ? `?year=${year}` : '';
  const { data } = await api.post(
    `/absence-balances/employee/${employeeId}/policy/${absenceTypeId}/recalculate${params}`
  );
  return data;
}

export interface MigrateBalanceResult {
  migratedEmployees: number;
  movedBalances: number;
  movedAssignments: number;
  movedHistoryRecords: number;
  movedAuditLogs: number;
  overrideMappingsWritten: number;
}

/**
 * Move a group of employees' balance, history, and assignment from one absence
 * type into another, and lock in a per-employee PeopleForce routing override
 * so subsequent PF syncs don't drag them back to the source type.
 */
export async function migrateBalanceBetweenAbsenceTypes(
  targetAbsenceTypeId: string,
  sourceAbsenceTypeId: string,
  employeeIds: string[]
): Promise<MigrateBalanceResult> {
  const { data } = await api.post<MigrateBalanceResult>(
    `/absence-types/${targetAbsenceTypeId}/migrate-balance`,
    { sourceAbsenceTypeId, employeeIds }
  );
  return data;
}


export interface AdjustBalanceInput {
  amount: number;
  reason: string;
  performedById?: string;
}

export async function adjustBalance(
  employeeId: string,
  absenceTypeId: string,
  input: AdjustBalanceInput,
  year?: number,
): Promise<void> {
  const params = year ? `?year=${year}` : '';
  await api.post(
    `/absence-balances/employee/${employeeId}/policy/${absenceTypeId}/adjust${params}`,
    input,
  );
}

// ============================================
// EMPLOYEE ABSENCES (History)
// ============================================

export interface EmployeeAbsence {
  id: string;
  /** Where the record came from. 'local' = created in this app via the request
   *  workflow; 'peopleforce' = read-only history imported from PeopleForce. */
  source: 'local' | 'peopleforce';
  absenceTypeId: string | null;
  absenceType: {
    id: string;
    name: string;
    color: string;
    isPaid: boolean;
  } | null;
  /** Fallback name for imported records whose PF leave type isn't mapped to a
   *  local AbsenceType yet. Null for local records and mapped imports. */
  pfLeaveTypeName: string | null;
  startDate: string;
  endDate: string;
  days: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'recalled';
  notes?: string | null;
  createdAt: string;
  /** ISO timestamp when a PeopleForce-imported record was voided by HR, or null
   *  if it's still live. Always null for local records. */
  cancelledAt: string | null;
}

export async function getEmployeeAbsences(
  employeeId: string,
  year?: number
): Promise<EmployeeAbsence[]> {
  const params = year ? `?year=${year}` : '';
  const { data } = await api.get<EmployeeAbsence[]>(`/absences/employee/${employeeId}${params}`);
  return data;
}

/**
 * Void a PeopleForce-imported absence record, returning its days to the
 * employee's balance. HR admin / super admin only (enforced server-side via
 * `absences.requests.manage`).
 */
export async function cancelImportedAbsence(recordId: string): Promise<void> {
  await api.post(`/absences/imported/${recordId}/cancel`);
}

// ============================================
// PEOPLEFORCE LEAVE TYPES (Real-time balances)
// ============================================

export interface PeopleForceLeaveType {
  absenceTypeId: string;
  name: string;
  color: string | null;
  unlimitedBalance: boolean;
  balance: number;
  accruedDays: number;
  usedDays: number;
  pendingDays: number;
  carriedOverDays: number;
  year: number;
}

export interface PeopleForceLeaveTypesResponse {
  success: boolean;
  data: PeopleForceLeaveType[];
}

export async function getMyPeopleForceLeaveTypes(): Promise<PeopleForceLeaveTypesResponse> {
  const { data } = await api.get<PeopleForceLeaveTypesResponse>('/peopleforce/my-leave-types');
  return data;
}

// ============================================
// ABSENCE REQUESTS
// ============================================

export interface CreateAbsenceRequestInput {
  employeeId: string;
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  halfDayStart?: boolean;
  halfDayEnd?: boolean;
  isUrgent?: boolean;
  notes?: string;
}

export interface AbsenceRequestResponse {
  id: string;
  employeeId: string;
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export async function createAbsenceRequest(
  input: CreateAbsenceRequestInput
): Promise<AbsenceRequestResponse> {
  const { data } = await api.post<AbsenceRequestResponse>('/absence-requests', input);
  return data;
}

export interface UpdateAbsenceRequestInput {
  absenceTypeId?: string;
  startDate?: string;
  endDate?: string;
  totalDays?: number;
  halfDayStart?: boolean;
  halfDayEnd?: boolean;
  notes?: string;
}

/** Full request detail used to pre-populate the edit dialog (includes half-day
 *  flags, which the unified history feed omits). */
export interface AbsenceRequestDetail {
  id: string;
  employeeId: string;
  employee: { firstName: string | null; lastName: string | null };
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  notes: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'RECALLED';
}

export async function getAbsenceRequest(id: string): Promise<AbsenceRequestDetail> {
  const { data } = await api.get<AbsenceRequestDetail>(`/absence-requests/${id}`);
  return data;
}

export async function updateAbsenceRequest(
  id: string,
  input: UpdateAbsenceRequestInput
): Promise<AbsenceRequestResponse> {
  const { data } = await api.put<AbsenceRequestResponse>(`/absence-requests/${id}`, input);
  return data;
}

export async function deleteAbsenceRequest(id: string): Promise<void> {
  await api.delete(`/absence-requests/${id}`);
}

// ============================================
// ABSENCE CALENDAR
// ============================================

export interface CalendarAbsence {
  id: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  notes?: string;
  absenceType: {
    id: string;
    name: string;
    color: string;
    isPaid: boolean;
  };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    department?: { name: string };
  };
}

export interface CalendarQueryParams {
  startDate: string;
  endDate: string;
  departmentId?: string;
}

export async function getAbsenceCalendar(params: CalendarQueryParams): Promise<CalendarAbsence[]> {
  const searchParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.departmentId) {
    searchParams.set('departmentId', params.departmentId);
  }
  const { data } = await api.get<CalendarAbsence[]>(
    `/absences/calendar?${searchParams.toString()}`
  );
  return data;
}

// ============================================
// CALENDAR EVENTS (PeopleForce Sync)
// ============================================

export type CalendarEventType =
  | 'birthday'
  | 'leave_request'
  | 'first_day'
  | 'last_day'
  | 'last_probation_day';

export interface CalendarEvent {
  id: string;
  type: CalendarEventType;
  startsOn: string;
  endsOn: string;
  employeeId: string | null;
  // Name parts — format via useEmployeeNameFormatter(), never render raw (HW-729).
  employeeFirstName: string | null;
  employeeLastName: string | null;
  employeePreferredName: string | null;
  employeeAvatar: string | null;
  departmentName: string | null;
  absenceTypeId: string | null;
  absenceTypeName: string | null;
  absenceTypeColor: string | null;
  // HW-701: 'approved' → solid fill, 'pending' → outline (no fill). PF-synced
  // events and non-leave types are always 'approved'.
  status: 'approved' | 'pending';
}

export interface CalendarAbsenceTypeInfo {
  id: string;
  name: string;
  color: string | null;
  isPaid: boolean;
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  absenceTypes: CalendarAbsenceTypeInfo[];
}

export interface CalendarEventsQueryParams {
  startDate: string;
  endDate: string;
  departmentId?: string;
  types?: CalendarEventType[];
}

export async function getCalendarEvents(
  params: CalendarEventsQueryParams
): Promise<CalendarEventsResponse> {
  const searchParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.departmentId) {
    searchParams.set('departmentId', params.departmentId);
  }
  if (params.types && params.types.length > 0) {
    searchParams.set('types', params.types.join(','));
  }
  const { data } = await api.get<CalendarEventsResponse>(
    `/absence-calendar/events?${searchParams.toString()}`
  );
  return data;
}

// ============================================
// HR BALANCES OVERVIEW
// ============================================

export interface BalancesOverviewTypeOption {
  id: string;
  name: string;
  color: string | null;
  accrualEnabled: boolean;
}

export interface BalancesOverviewRow {
  employee: {
    id: string;
    // Name parts — format via useEmployeeNameFormatter(), never render raw (HW-729).
    firstName: string | null;
    lastName: string | null;
    preferredName: string | null;
    avatarUrl: string | null;
    department: string | null;
    position: string | null;
  };
  balance: {
    available: number | null; // null = unlimited type
    accrued: number;
    used: number;
    pending: number;
    carriedOver: number;
  };
  accrual: { nextDate: string | null; nextAmount: number } | null;
}

export interface BalancesOverviewResponse {
  absenceType: {
    id: string;
    name: string;
    color: string | null;
    unlimitedBalance: boolean;
    accrualEnabled: boolean;
  };
  year: number;
  rows: BalancesOverviewRow[];
}

export async function getBalancesOverviewTypes(): Promise<BalancesOverviewTypeOption[]> {
  const { data } = await api.get<BalancesOverviewTypeOption[]>('/absence-balances/overview/types');
  return data;
}

export async function getBalancesOverview(
  typeId: string,
  year: number
): Promise<BalancesOverviewResponse> {
  const { data } = await api.get<BalancesOverviewResponse>('/absence-balances/overview', {
    params: { typeId, year },
  });
  return data;
}

// ============================================
// BACKWARD COMPATIBILITY ALIASES
// ============================================

// These aliases help during migration from policy-based to absence-type-based API
// Can be removed once all frontend code is updated

/** @deprecated Use AbsenceType instead */
export type AbsencePolicy = AbsenceType;

/** @deprecated Use AbsenceTypeTenureLevel instead */
export type PolicyTenureLevel = AbsenceTypeTenureLevel;

/** @deprecated Use AbsenceTypeAssignment instead */
export type PolicyAssignment = AbsenceTypeAssignment;

/** @deprecated Use AbsenceTypeApprover instead */
export type PolicyApprover = AbsenceTypeApprover;

/** @deprecated Use getAbsenceType instead */
export const getAbsencePolicy = getAbsenceType;

/** @deprecated Use updateAbsenceType instead */
export const updateAbsencePolicy = updateAbsenceType;

/** @deprecated Use EmployeeAssignedAbsenceType instead */
export type EmployeeAssignedPolicy = EmployeeAssignedAbsenceType;

/** @deprecated Use getEmployeeAssignedAbsenceTypes instead */
export const getEmployeeAssignedPolicies = getEmployeeAssignedAbsenceTypes;

/** @deprecated Use batchAssignAbsenceType instead */
export const batchAssignPolicy = batchAssignAbsenceType;

/** @deprecated Use addAbsenceTypeTenureLevel instead */
export const addPolicyTenureLevel = addAbsenceTypeTenureLevel;

/** @deprecated Use updateAbsenceTypeTenureLevel instead */
export const updatePolicyTenureLevel = updateAbsenceTypeTenureLevel;

/** @deprecated Use deleteAbsenceTypeTenureLevel instead */
export const deletePolicyTenureLevel = deleteAbsenceTypeTenureLevel;

/** @deprecated Use assignAbsenceTypeToAll instead */
export const assignPolicyToAll = assignAbsenceTypeToAll;

/** @deprecated Use unassignAbsenceTypeFromAll instead */
export const unassignPolicyFromAll = unassignAbsenceTypeFromAll;

/** @deprecated Use assignAbsenceTypeToEmployee instead */
export const assignPolicyToEmployee = assignAbsenceTypeToEmployee;

/** @deprecated Use getAbsenceTypeAssignments instead */
export const getPolicyAssignments = getAbsenceTypeAssignments;

/** @deprecated Use removeAbsenceTypeAssignment instead */
export const removePolicyAssignment = removeAbsenceTypeAssignment;
